import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import type { SingleValue } from 'react-select';

import { Button, Select, Text } from '@revelio/ui';

import type {
  DataBuilderModel,
  DataBuilderModelVersions,
} from '../../api/data-builder-api.types';
import {
  type ConditionContext,
  evaluateCondition,
} from '../../api/evaluate-condition';
import type { ModelVersionSelections } from '../../data-builder.store';
import {
  getSelectedModelVersion,
  getSelectedOption,
  getVisibleVersionOptions,
  isLatestVersions,
  type VersionOption,
} from './model-utils';

type ModelVersionsPopoverProps = {
  models: DataBuilderModel[];
  modelVersions: ModelVersionSelections;
  defaults: ModelVersionSelections;
  settings?: ConditionContext['settings'];
  onSetModelVersion: (
    key: keyof DataBuilderModelVersions,
    value: number | string | undefined
  ) => void;
  onResetModelVersions: (defaults: ModelVersionSelections) => void;
};

export const ModelVersionsPopover = ({
  models,
  modelVersions,
  defaults,
  settings,
  onSetModelVersion,
  onResetModelVersions,
}: ModelVersionsPopoverProps) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (
        triggerRef.current?.contains(target) === true ||
        contentRef.current?.contains(target) === true
      ) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen, onClose]);

  const visibleModels = models.filter((model) =>
    evaluateCondition(model.condition, {
      models: modelVersions,
      settings,
    })
  );
  const isLatest = isLatestVersions(modelVersions, visibleModels);

  if (visibleModels.length === 0) {
    return null;
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnBlur={false}
      autoFocus={false}
      returnFocusOnClose={false}
      isLazy
    >
      <PopoverTrigger>
        <div ref={triggerRef}>
          <Button
            rightIcon={<ChevronDownIcon />}
            size="sm"
            variant="outlined"
            style={{ fontWeight: 500 }}
            onClick={onToggle}
          >
            <Box>Models</Box>
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent ref={contentRef} width="320px">
        <PopoverHeader
          fontSize="sm"
          fontWeight="semibold"
          color="navyBlue.500"
          borderBottom="1px"
          borderColor="gray.200"
        >
          Model Versions
        </PopoverHeader>
        <PopoverBody>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {visibleModels.map((model) => {
              const selectedValue = getSelectedModelVersion(
                model,
                modelVersions
              );
              const options = getVisibleVersionOptions(
                model,
                modelVersions,
                settings
              );
              const selectedOption = getSelectedOption(options, selectedValue);

              if (selectedOption === null) {
                return null;
              }

              return (
                <div key={model.key}>
                  <div style={{ marginBottom: '4px' }}>
                    <Text as="label" fontSize="sm" weight="semibold">
                      {model.name}
                    </Text>
                  </div>
                  <Select<VersionOption>
                    value={selectedOption}
                    options={options}
                    onChange={(option: SingleValue<VersionOption>) => {
                      if (option === null) {
                        return;
                      }

                      onSetModelVersion(
                        model.key as keyof DataBuilderModelVersions,
                        option.value
                      );
                    }}
                    isSearchable={false}
                    size="sm"
                    data-testid={`data-builder-model-version-${model.key}`}
                  />
                </div>
              );
            })}
            {!isLatest && (
              <div>
                <Button
                  size="xs"
                  variant="ghost"
                  color="destructive"
                  onClick={() =>
                    onResetModelVersions({
                      ...modelVersions,
                      ...defaults,
                    })
                  }
                >
                  Reset to Latest
                </Button>
              </div>
            )}
          </div>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
